import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Bus, RefreshCw, MapPin, Wifi, WifiOff, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const loadGoogleMaps = (apiKey) => new Promise((resolve, reject) => {
  if (window.google && window.google.maps) {
    resolve();
    return;
  }

  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    existingScript.addEventListener('load', resolve, { once: true });
    existingScript.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=marker`;
  script.async = true;
  script.defer = true;
  script.onload = resolve;
  script.onerror = reject;
  document.head.appendChild(script);
});

const wsUrl = () => {
  const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProto}//${window.location.host}/api/ws/fleet`;
};

const statusColor = (s) => {
  if (s === 'in_transit') return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
};

const createBusIcon = (heading = 0, color = '#059669') => L.divIcon({
  className: 'custom-leaflet-bus-icon',
  html: `<div style="transform: rotate(${heading}deg); width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; background: ${color}; color: #ffffff; border: 2px solid #ffffff; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.35);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const LiveFleetMap = () => {
  const navigate = useNavigate();
  const { token, isOverseer, loading: authLoading } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [mapsApiKey, setMapsApiKey] = useState('');
  const [mapsConfigLoaded, setMapsConfigLoaded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});   // trip.id -> L.marker

  useEffect(() => {
    if (!authLoading && (!token || !isOverseer())) {
      navigate('/login');
      toast.error('Access denied. Admin privileges required.');
    }
  }, [authLoading, token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/integrations/maps/browser-key`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      setMapsApiKey(response.data.api_key || '');
    }).catch(() => {
      setMapsApiKey('');
    }).finally(() => {
      setMapsConfigLoaded(true);
    });
  }, [token]);

  // Initialise Leaflet OpenStreetMap once
  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      if (!mapsConfigLoaded || !mapContainerRef.current || mapRef.current) return;

      if (mapsApiKey) {
        try {
          await loadGoogleMaps(mapsApiKey);
          if (cancelled || !mapContainerRef.current) return;
          const map = new window.google.maps.Map(mapContainerRef.current, {
            center: { lat: -22.0, lng: 29.0 },
            zoom: 6,
            mapTypeId: 'roadmap',
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
          });
          mapRef.current = map;
          mapRef.current.__googleMap = true;
          return;
        } catch (e) {
          console.warn('Google Maps failed to load, falling back to OpenStreetMap:', e);
        }
      }

      const map = L.map(mapContainerRef.current, {
        center: [-22.0, 29.0],
        zoom: 6,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);

      mapRef.current = map;
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapRef.current && mapRef.current.__googleMap) {
        mapRef.current = null;
        return;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapsApiKey, mapsConfigLoaded]);

  // Reconcile markers to trips
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const isGoogleMap = !!map.__googleMap;
    const seen = new Set();
    const latLngs = [];

    trips.forEach((t) => {
      const lat = t.gps_location?.lat;
      const lng = t.gps_location?.lng;
      if (typeof lat !== 'number' || typeof lng !== 'number') return;
      seen.add(t.id);
      latLngs.push([lat, lng]);

      const color = t.status === 'in_transit' ? '#059669' : '#64748b';
      const heading = t.gps_bearing || 0;
      const popupHtml = `
        <div style="font-family: system-ui, sans-serif; min-width: 180px; padding: 2px;">
          <div style="font-weight:700; color:#0f172a; font-size: 14px;">${t.route_name || 'Route'}</div>
          <div style="font-size:12px; color:#64748b; margin-top:2px;">Driver: ${t.driver_name || 'Unassigned'}</div>
          <div style="font-size:12px; color:#64748b;">Bus: ${t.bus_registration || t.bus_id || '—'}</div>
          <div style="font-size:12px; font-weight:600; color:${color}; margin-top:6px;">
            ${(t.status || '').toUpperCase()}
            ${typeof t.progress_percent === 'number' ? ` · ${Math.round(t.progress_percent)}%` : ''}
          </div>
        </div>`;

      if (isGoogleMap) {
        const position = { lat, lng };
        const existing = markersRef.current[t.id];
        const icon = {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 7,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          rotation: heading,
        };

        if (existing) {
          existing.setPosition(position);
          existing.setIcon(icon);
          existing.infowindow.setContent(popupHtml);
          return;
        }

        const marker = new window.google.maps.Marker({
          map,
          position,
          title: t.route_name || 'Fleet bus',
          icon,
          zIndex: 1000,
        });

        const info = new window.google.maps.InfoWindow({ content: popupHtml });
        marker.addListener('click', () => info.open({ anchor: marker, map }));
        markersRef.current[t.id] = { ...marker, infowindow: info };
        return;
      }

      const busIcon = createBusIcon(heading, color);
      const existing = markersRef.current[t.id];
      if (existing) {
        existing.setLatLng([lat, lng]);
        existing.setIcon(busIcon);
        existing.setPopupContent(popupHtml);
      } else {
        const m = L.marker([lat, lng], { icon: busIcon }).addTo(map);
        m.bindPopup(popupHtml);
        markersRef.current[t.id] = m;
      }
    });

    // Prune stale markers
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) {
        if (isGoogleMap) {
          markersRef.current[id].setMap(null);
        } else {
          map.removeLayer(markersRef.current[id]);
        }
        delete markersRef.current[id];
      }
    });

    if (latLngs.length > 0) {
      if (isGoogleMap) {
        const bounds = new window.google.maps.LatLngBounds();
        latLngs.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
        map.fitBounds(bounds, 50);
      } else {
        const bounds = L.latLngBounds(latLngs);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [trips]);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/enterprise/realtime/fleet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrips(r.data?.trips || []);
      setLastUpdate(r.data?.as_of);
    } catch (e) {
      toast.error('Failed to load fleet snapshot');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    let retries = 0;
    let pingTimer = null;

    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(wsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          retries = 0;
          pingTimer = setInterval(() => { try { ws.send('ping'); } catch (_) { /* ignore */ } }, 25000);
        };
        ws.onmessage = (msg) => {
          try {
            const ev = JSON.parse(msg.data);
            if (ev.type === 'fleet_update' && ev.trip_id) {
              setTrips(prev => {
                const idx = prev.findIndex(t => t.id === ev.trip_id);
                if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = { ...next[idx], ...ev };
                  return next;
                }
                return [...prev, ev];
              });
              setLastUpdate(ev.ts);
            }
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => {
          setConnected(false);
          if (pingTimer) clearInterval(pingTimer);
          if (alive) {
            retries++;
            const delay = Math.min(15000, 1000 * Math.pow(2, retries));
            setTimeout(connect, delay);
          }
        };
      } catch (_) {
        setConnected(false);
      }
    };

    connect();
    return () => {
      alive = false;
      if (pingTimer) clearInterval(pingTimer);
      try { wsRef.current?.close(); } catch (_) { /* ignore */ }
    };
  }, [token]);

  const tripsWithGps = trips.filter(t => typeof t.gps_location?.lat === 'number');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              data-testid="fleet-back-btn"
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Live Fleet Command
            </h1>
            <p className="text-slate-600">Real-time view of scheduled and in-transit buses (OpenStreetMap)</p>
          </div>
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <Wifi className="h-3 w-3 mr-1" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-slate-400">
                <WifiOff className="h-3 w-3 mr-1" /> Offline
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="fleet-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Active Trips</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="fleet-active-count">{trips.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">In Transit</p>
              <p className="text-2xl font-bold text-emerald-600">
                {trips.filter(t => t.status === 'in_transit').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">With GPS</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="fleet-gps-count">{tripsWithGps.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Last Update</p>
              <p className="text-sm font-bold text-slate-900">
                {lastUpdate ? new Date(lastUpdate).toLocaleTimeString() : '—'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* OpenStreetMap Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" /> OpenStreetMap Fleet Tracker
              <Badge variant="outline" className="ml-auto text-xs">
                {tripsWithGps.length} on map
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <div
                ref={mapContainerRef}
                className="w-full h-[420px] rounded-lg border z-0"
                data-testid="fleet-map-container"
              />
              {tripsWithGps.length === 0 && (
                <div className="absolute inset-x-0 top-4 mx-auto max-w-md pointer-events-none z-10">
                  <div className="bg-white/90 border rounded-lg shadow-sm px-3 py-2 text-center">
                    <p className="text-sm text-slate-600">No GPS pings yet — buses will appear once they emit location.</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Fleet Roster */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Bus className="h-5 w-5" />
              Fleet Roster
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && trips.length === 0 && (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}
              </div>
            )}
            {!loading && trips.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No active or scheduled trips at the moment.</p>
                <p className="text-xs mt-1">Trips appear here according to their scheduled departure status.</p>
              </div>
            )}
            {trips.length > 0 && (
              <div className="space-y-2">
                {trips.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                    data-testid={`fleet-trip-${t.id}`}
                    onClick={() => {
                      const m = markersRef.current[t.id];
                      if (m && mapRef.current) {
                        const lat = t.gps_location?.lat;
                        const lng = t.gps_location?.lng;
                        if (typeof lat === 'number' && typeof lng === 'number') {
                          mapRef.current.panTo([lat, lng]);
                          mapRef.current.setZoom(12);
                          m.openPopup();
                        }
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                        <Bus className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{t.route_name || 'Route'}</p>
                        <p className="text-xs text-slate-500">
                          {t.driver_name ? `${t.driver_name} · ` : ''}
                          Bus {t.bus_id?.slice(0, 8) || '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {typeof t.progress_percent === 'number' && (
                        <div className="hidden md:block w-24">
                          <div className="h-1.5 bg-slate-200 rounded">
                            <div
                              className="h-1.5 bg-emerald-500 rounded transition-all"
                              style={{ width: `${Math.min(100, Math.max(0, t.progress_percent))}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-slate-500 mt-0.5 text-right">{Math.round(t.progress_percent)}%</p>
                        </div>
                      )}
                      {t.gps_location && (
                        <div className="hidden md:flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="h-3 w-3" />
                          {(typeof t.gps_location?.lat === 'number') ?
                            `${t.gps_location.lat.toFixed(2)}, ${t.gps_location.lng.toFixed(2)}` : 'GPS'}
                        </div>
                      )}
                      <Badge className={statusColor(t.status)}>{t.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default LiveFleetMap;
